// Legendary actions where the DM is already looking: a pip per action on the
// creature's own tracker row, and a strip between turns naming who can still
// act. Lair actions sit in the same strip, because "what else happens before
// the next turn" is one question however the rules group it.
//
// Tracking only — no rolling, no targets. See state/legendary.ts.
import { useStore } from "../state/store";
import { canAct, giveBackLegendary, lairPending, legendaryOf, markLairActed, setLair, setLegendary, spendLegendary, lairOf } from "../state/legendary";

/** The control on a creature's row: give it a budget, then spend from it. */
export function LegendaryPips({ row, incapacitated }: { row: { id: string; name: string }; incapacitated: boolean }) {
  const { layout, update, logActivity } = useStore();
  const round = Number(layout.roundNumber || 1);
  const state = legendaryOf(layout)[row.id];
  const lair = Boolean(lairOf(layout)[row.id]);
  if (!state) return (
    <span className="legendary-add">
      <select className="xs" value="" aria-label={`Give ${row.name} legendary actions`} title="Legendary actions: a budget per round, spent between other creatures' turns and refilled at the start of this creature's own. Tracking only — nothing is rolled."
        onChange={(e) => { const n = Number(e.target.value); if (!n) return; update((l) => setLegendary(l, row, n, round)); logActivity({ text: `${row.name}: ${n} legendary action${n === 1 ? "" : "s"} a round`, tone: "violet" }); }}>
        <option value="">+ legendary</option>
        {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} a round</option>)}
      </select>
      <button className={`ghost small ${lair ? "on" : ""}`} aria-pressed={lair} title={lair ? `${row.name} has lair actions: they act on initiative 20 each round. Click to stop.` : `${row.name} has lair actions on initiative 20`}
        onClick={() => { update((l) => setLair(l, row, !lair, round)); logActivity({ text: `${row.name}: lair actions ${lair ? "off" : "on initiative 20"}` }); }}>🏔</button>
    </span>
  );
  return (
    <span className="legendary" title={`${state.left} of ${state.budget} legendary actions left this round. They refill at the start of ${row.name}'s turn and cannot be spent on it.${incapacitated ? " Incapacitated: none may be spent." : ""}`}>
      <button className="ghost small" aria-label={`Spend a legendary action for ${row.name}`} disabled={state.left < 1 || incapacitated}
        onClick={() => { update((l) => spendLegendary(l, row.id)); logActivity({ text: `${row.name} takes a legendary action`, meta: `${state.left - 1} of ${state.budget} left this round`, tone: "amber" }); }}>
        {"◆".repeat(state.left)}{"◇".repeat(Math.max(0, state.budget - state.left))}
      </button>
      <button className="link small" aria-label={`Give back a legendary action to ${row.name}`} title="Put one back (a misclick)" disabled={state.left >= state.budget}
        onClick={() => update((l) => giveBackLegendary(l, row.id))}>+</button>
      <button className="link small" aria-label={`${row.name} has no legendary actions`} title="No legendary actions" onClick={() => { update((l) => setLegendary(l, row, 0, round)); logActivity({ text: `${row.name}: legendary actions off` }); }}>×</button>
    </span>
  );
}

/** Between turns: who can still act, and whether the lair has. */
export function LegendaryStrip({ currentTurnId, incapacitated }: { currentTurnId: string; incapacitated: (id: string) => boolean }) {
  const { layout, update, logActivity } = useStore();
  const round = Number(layout.roundNumber || 1);
  const ready = canAct(layout, currentTurnId, incapacitated);
  const lairs = lairPending(layout, round);
  if (!ready.length && !lairs.length) return null;
  return (
    <div className="legendary-strip" role="status">
      {lairs.map((l) => (
        <span key={l.id} className="apply-hit" title="A lair action happens on initiative count 20, losing ties, once each round.">
          <span>🏔 <strong>{l.name}</strong> — lair action, round {round}</span>
          <button className="small primary" onClick={() => { update((s) => markLairActed(s, l.id, round)); logActivity({ text: `${l.name}: lair action taken`, meta: `round ${round}`, tone: "amber" }); }}>taken</button>
        </span>
      ))}
      {ready.length > 0 && (
        <span className="apply-hit" title="Legendary actions left this round. They are spent between other creatures' turns.">
          <span>◆ legendary:</span>
          {ready.map((s) => (
            <button key={s.id} className="small" onClick={() => { update((l) => spendLegendary(l, s.id)); logActivity({ text: `${s.name} takes a legendary action`, meta: `${s.left - 1} of ${s.budget} left this round`, tone: "amber" }); }}>
              {s.name} {s.left}
            </button>
          ))}
        </span>
      )}
    </div>
  );
}
